/**
 * cmp-variant-selector.js
 * Pairs with snippets/cmp-variant-selector.liquid ("Variant Selector" block
 * of sections/custom-main-product.liquid).
 *
 * Forked from assets/custom-variant-selector.js (cvs-* → cmp-*) rather than
 * editing that file in place, since it's used by the LIVE
 * sections/main-product.liquid (block type custom_variant_selector).
 *
 * This file is purely visual/UX sugar on top of a real <variant-selects>
 * element with real radio inputs. It never fetches a variant, never touches
 * price/cart/inventory, and never replaces Dawn's own native 'change' →
 * optionValueSelectionChange → product-info.js pipeline.
 *
 * Responsibilities:
 *  1. Two-directional dimming: mark Flavor cards that have no available
 *     variant at the currently-selected Size (and vice versa).
 *  2. If a user's click leaves an invalid (nonexistent/sold-out) pairing,
 *     auto-correct the OTHER dimension using the same "first available
 *     flavor / last available size" rule, and dispatch a real 'change'
 *     event on the corrected input so the native pipeline picks up the
 *     final, valid combination.
 *  3. Our own default-selection rule (first available flavor, then last
 *     available size) deliberately differs from Shopify's native
 *     `selected_or_first_available_variant`, so on a plain product-URL
 *     load the rest of the page (price, buy button, inventory) can be
 *     server-rendered for the WRONG variant. If our default differs from
 *     the native one, fire one synthetic 'change' on load so the existing
 *     native AJAX pipeline reconciles everything to our default.
 *  4. Dawn's product-info.js re-renders this section after every click
 *     and REPLACES this <variant-selects> node with the server's HTML. So:
 *     (a) every section re-fetch carries &variant=<the shopper's pick> so
 *         the Liquid honors it (product.selected_variant) instead of
 *         re-applying the default rule and snapping back to it, and
 *     (b) each replacement node is re-initialised (listeners, dimming,
 *         backorder) — otherwise everything stops working after one click.
 *  5. Backorder button text ("Backorder", still a real submit button) +
 *     the bold message below it + the hero badges (snippets/
 *     cmp-media-badges.liquid, the collection card's two-slot logic), all
 *     driven by the same availability blob, re-applied on init and on
 *     every native variantChange.
 */
(function () {
  function isRadio(el) {
    return !!el && el.classList && el.classList.contains('cmp-card__input');
  }

  function initCmpVariantSelector(root, isInitialLoad) {
    var availabilityScript = root.querySelector('[data-cmp-availability]');
    if (!availabilityScript) return;

    var data;
    try {
      data = JSON.parse(availabilityScript.textContent);
    } catch (e) {
      return;
    }

    var flavorFieldset = root.querySelector('[data-cmp-fieldset="flavor"]');
    var sizeFieldset = root.querySelector('[data-cmp-fieldset="size"]');

    function getChecked(fieldset) {
      if (!fieldset) return null;
      var input = fieldset.querySelector('.cmp-card__input:checked');
      return input ? input.value : null;
    }

    function isPairAvailable(flavor, size) {
      return data.variants.some(function (v) {
        var flavorMatch = !data.hasFlavor || v.flavor === flavor;
        var sizeMatch = !data.hasSize || v.size === size;
        return flavorMatch && sizeMatch && v.available;
      });
    }

    function firstAvailableFlavorFor(size) {
      if (!flavorFieldset) return null;
      var inputs = flavorFieldset.querySelectorAll('.cmp-card__input');
      for (var i = 0; i < inputs.length; i++) {
        if (isPairAvailable(inputs[i].value, size)) return inputs[i].value;
      }
      return null;
    }

    function lastAvailableSizeFor(flavor) {
      if (!sizeFieldset) return null;
      var inputs = sizeFieldset.querySelectorAll('.cmp-card__input');
      var match = null;
      inputs.forEach(function (input) {
        if (isPairAvailable(flavor, input.value)) match = input.value;
      });
      return match;
    }

    function selectedVariantId() {
      var flavor = getChecked(flavorFieldset);
      var size = getChecked(sizeFieldset);
      var match = data.variants.filter(function (v) {
        return (!data.hasFlavor || v.flavor === flavor) && (!data.hasSize || v.size === size);
      })[0];
      return match ? match.id : null;
    }
    root.cmpSelectedVariantId = selectedVariantId;

    function applyDimming() {
      var currentFlavor = getChecked(flavorFieldset);
      var currentSize = getChecked(sizeFieldset);

      if (flavorFieldset && data.hasSize) {
        flavorFieldset.querySelectorAll('.cmp-card__input').forEach(function (input) {
          var card = input.nextElementSibling;
          if (!card) return;
          card.classList.toggle('cmp-card--dimmed', !isPairAvailable(input.value, currentSize));
        });
      }

      if (sizeFieldset && data.hasFlavor) {
        sizeFieldset.querySelectorAll('.cmp-card__input').forEach(function (input) {
          var card = input.nextElementSibling;
          if (!card) return;
          card.classList.toggle('cmp-card--dimmed', !isPairAvailable(currentFlavor, input.value));
        });
      }
    }

    function setChecked(fieldset, value) {
      if (!fieldset || value === null || value === undefined) return;
      var inputs = fieldset.querySelectorAll('.cmp-card__input');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].value === value) {
          if (!inputs[i].checked) {
            inputs[i].checked = true;
            inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      }
    }

    root.addEventListener('change', function (event) {
      if (!isRadio(event.target)) return;

      var currentFlavor = getChecked(flavorFieldset);
      var currentSize = getChecked(sizeFieldset);

      if (data.hasFlavor && data.hasSize && !isPairAvailable(currentFlavor, currentSize)) {
        var changedInSize = sizeFieldset && sizeFieldset.contains(event.target);
        if (changedInSize) {
          setChecked(flavorFieldset, firstAvailableFlavorFor(currentSize));
        } else {
          setChecked(sizeFieldset, lastAvailableSizeFor(currentFlavor));
        }
        return;
      }

      applyDimming();
    });

    applyDimming();

    // ── Backorder: button text, bold message, PDP-only hero badge ────────
    var sectionId = data.sectionId || root.dataset.section;
    var submitButton = document.getElementById('ProductSubmitButton-' + sectionId);
    // The label is the button's first <span> that isn't buy-buttons.liquid's
    // price total / divider (which come first), or "Backorder" lands in the
    // total slot.
    var submitButtonLabel = submitButton
      ? submitButton.querySelector(':scope > span:not([data-atc-total-price]):not([data-atc-divider])')
      : null;

    // The message markup lives inside <variant-selects> (so root.querySelector
    // can find it), but it must visually sit below the Add to Cart button,
    // which is a separate, later block. Relocate the one existing node once
    // on init rather than duplicating the block's default-variant computation
    // just to render it in the right place server-side.
    // On a re-render, the previous render's relocated message is no longer
    // inside any <variant-selects>, so it would otherwise stack up.
    document.querySelectorAll('[data-cmp-backorder-message][data-cmp-section="' + sectionId + '"]').forEach(function (stale) {
      stale.remove();
    });
    var backorderMessage = root.querySelector('[data-cmp-backorder-message]');
    if (backorderMessage) backorderMessage.setAttribute('data-cmp-section', sectionId);
    if (backorderMessage && submitButton) {
      var buttonWrapper = submitButton.closest('.product-form__buttons') || submitButton.parentElement;
      if (buttonWrapper && buttonWrapper.parentElement) {
        buttonWrapper.parentElement.insertBefore(backorderMessage, buttonWrapper.nextSibling);
      }
    }
    var backorderMessageText = backorderMessage ? backorderMessage.querySelector('[data-cmp-backorder-text]') : null;
    var badgeStack = document.querySelector('[data-cmp-badges]');
    var badgeTop = badgeStack ? badgeStack.querySelector('[data-cmp-badge-slot="top"]') : null;
    var badgeBottom = badgeStack ? badgeStack.querySelector('[data-cmp-badge-slot="bottom"]') : null;
    // Product-level values from the server render — not the slots' current
    // text, which already reflects whichever variant was rendered.
    var badgeStrength = badgeStack ? badgeStack.getAttribute('data-cmp-badge-strength') || '' : '';
    var productLabel = badgeStack ? badgeStack.getAttribute('data-cmp-badge-label') || '' : '';

    // Mirrors snippets/cmp-media-badges.liquid and the collection card's
    // resolveBadgeSlots() (assets/card-product-custom.js) — keep in sync.
    function resolveBadgeSlots(variant) {
      var label = (variant && variant.label) || productLabel;
      var top = null;
      var bottom = null;
      if (variant && variant.backorder) {
        top = { text: 'Backorder', kind: 'backorder' };
        if (badgeStrength) bottom = { text: badgeStrength, kind: 'strength' };
        else if (label) bottom = { text: label, kind: 'label' };
      } else if (label) {
        top = { text: label, kind: 'label' };
        if (badgeStrength) bottom = { text: badgeStrength, kind: 'strength' };
      } else if (badgeStrength) {
        top = { text: badgeStrength, kind: 'strength' };
      }
      return { top: top, bottom: bottom };
    }

    function fillBadgeSlot(el, slot) {
      if (!el) return;
      el.textContent = slot ? slot.text : '';
      el.hidden = !slot;
      el.classList.toggle('cmp-media-badges__badge--outline', !!slot && slot.kind === 'label');
    }

    function variantById(id) {
      return (
        data.variants.filter(function (v) {
          return String(v.id) === String(id);
        })[0] || null
      );
    }

    function applyBackorderState(variant) {
      var backordered = !!(variant && variant.backorder);

      if (submitButtonLabel && !submitButton.hasAttribute('disabled')) {
        submitButtonLabel.textContent = backordered
          ? 'Backorder'
          : window.variantStrings && window.variantStrings.addToCart
          ? window.variantStrings.addToCart
          : submitButtonLabel.textContent;
      }

      if (backorderMessage) backorderMessage.hidden = !backordered;
      if (backorderMessageText) {
        backorderMessageText.textContent = backordered
          ? 'This item is backordered and will ship when back in stock.' +
            (variant.restockDate ? ' Estimated restock date ' + variant.restockDate + '.' : '')
          : '';
      }

      // No matching variant (e.g. a sold-out pick): leave the slots as they are.
      if (variant) {
        var badges = resolveBadgeSlots(variant);
        fillBadgeSlot(badgeTop, badges.top);
        fillBadgeSlot(badgeBottom, badges.bottom);
      }
    }

    // Our default rule (first available flavor, last available size) can
    // pick a different variant than Shopify's own selected_or_first_available_variant,
    // which the rest of the page (price, buy button, inventory) was
    // server-rendered against. Reconcile once on load, but never override
    // an explicit direct link to a specific variant/option combination.
    var selectedVariantScript = root.querySelector('[data-selected-variant]');
    var ourDefault = null;
    if (selectedVariantScript) {
      try {
        ourDefault = JSON.parse(selectedVariantScript.textContent);
      } catch (e) {
        ourDefault = null;
      }
    }

    applyBackorderState(ourDefault ? variantById(ourDefault.id) : null);

    var nativeDefaultId = root.dataset.nativeDefaultVariantId;
    var hasExplicitVariantParam = /[?&](variant|option_values)=/.test(window.location.search);
    if (isInitialLoad && ourDefault && nativeDefaultId && String(ourDefault.id) !== String(nativeDefaultId) && !hasExplicitVariantParam) {
      var checkedInput =
        (flavorFieldset && flavorFieldset.querySelector('.cmp-card__input:checked')) ||
        (sizeFieldset && sizeFieldset.querySelector('.cmp-card__input:checked'));
      if (checkedInput) checkedInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      if (unsubscribeBySection[sectionId]) unsubscribeBySection[sectionId]();
      unsubscribeBySection[sectionId] = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event.data.variant;
        applyBackorderState(variant ? variantById(variant.id) : null);
      });
    }
  }

  var unsubscribeBySection = {};

  // Make Dawn's section re-fetch carry the shopper's actual pick as
  // &variant=, so the re-rendered selector (and the price / buy button /
  // URL Dawn reads from it) reflects that pick, not our default rule.
  function patchProductInfo(productInfo) {
    if (!productInfo || productInfo.cmpPatched) return;
    var original = productInfo.buildRequestUrlWithParams;
    if (typeof original !== 'function') return;
    productInfo.cmpPatched = true;
    productInfo.buildRequestUrlWithParams = function (url, optionValues, shouldFetchFullPage) {
      var requestUrl = original.call(this, url, optionValues, shouldFetchFullPage);
      // Dawn inserts the new node BEFORE the old (hidden) one, so the first
      // match is always the current selector.
      var selects = this.querySelector('[data-cmp-variant-selects]');
      var variantId = selects && selects.cmpSelectedVariantId ? selects.cmpSelectedVariantId() : null;
      if (!variantId || url !== this.dataset.url) return requestUrl;
      return requestUrl + (requestUrl.indexOf('?') === -1 ? '?' : '&') + 'variant=' + encodeURIComponent(variantId);
    };
  }

  function init(root, isInitialLoad) {
    if (root.cmpInitialized) return;
    root.cmpInitialized = true;
    initCmpVariantSelector(root, isInitialLoad);

    var productInfo = root.closest('product-info');
    if (productInfo && window.customElements) {
      customElements.whenDefined('product-info').then(function () {
        patchProductInfo(productInfo);
      });
    }

    // Re-initialise whichever <variant-selects> Dawn swaps in next.
    var parent = root.parentNode;
    if (parent && !parent.cmpObserved && window.MutationObserver) {
      parent.cmpObserved = true;
      new MutationObserver(function () {
        parent.querySelectorAll(':scope > [data-cmp-variant-selects]').forEach(function (node) {
          init(node, false);
        });
      }).observe(parent, { childList: true });
    }
  }

  document.querySelectorAll('[data-cmp-variant-selects]').forEach(function (root) {
    init(root, true);
  });
})();
